import React from 'react';
import { MockWebsite as MockWebsiteType } from '../types/quiz';

interface MockWebsiteProps {
  mockWebsite: MockWebsiteType;
}

export const MockWebsite: React.FC<MockWebsiteProps> = ({ mockWebsite }) => {
  const renderEcommerce = () => (
    <div className="h-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col">
      {/* Header du site */}
      <div className="bg-linear-to-r from-orange-500 to-red-500 text-white p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🛒</span>
            <span className="font-bold text-lg">{mockWebsite.siteName || 'MegaDeals'}</span>
          </div>
          <div className="text-xs opacity-80">{mockWebsite.url || 'megadeals-shop.xyz'}</div>
        </div>
      </div>

      {/* Corps de la page */}
      <div className="flex-1 p-4 overflow-auto">
        {mockWebsite.content.urgency && (
          <div className="bg-red-100 border-2 border-red-500 text-red-800 p-2 mb-3 text-center text-sm font-bold animate-pulse">
            {mockWebsite.content.urgency}
          </div>
        )}

        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-3">
          {mockWebsite.content.images && mockWebsite.content.images.length > 0 && (
            <div className="bg-white dark:bg-gray-600 rounded mb-3 p-8 text-center">
              <div className="text-6xl">{mockWebsite.content.images[0]}</div>
            </div>
          )}

          <h3 className="font-bold text-lg mb-2 text-gray-800 dark:text-white">
            {mockWebsite.content.title || 'Produit en promotion'}
          </h3>

          {mockWebsite.content.price && (
            <div className="mb-3">
              <span className="text-3xl font-bold text-red-600 dark:text-red-400">
                {mockWebsite.content.price}
              </span>
              <span className="ml-2 text-gray-400 line-through">999€</span>
              <span className="ml-2 bg-green-500 text-white px-2 py-1 rounded text-sm">-70%</span>
            </div>
          )}

          {mockWebsite.content.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {mockWebsite.content.description}
            </p>
          )}

          {mockWebsite.content.badges && mockWebsite.content.badges.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {mockWebsite.content.badges.map((badge, idx) => (
                <span
                  key={idx}
                  className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-semibold"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}

          {mockWebsite.content.buttons && mockWebsite.content.buttons.length > 0 && (
            <div className="space-y-2">
              {mockWebsite.content.buttons.map((button, idx) => (
                <button
                  key={idx}
                  className={`w-full py-3 rounded font-bold text-white ${
                    button.style || 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {button.text}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Indicateurs de fraude */}
        {mockWebsite.redFlags && mockWebsite.redFlags.length > 0 && (
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div className="font-semibold">🚩 Signes suspects visibles :</div>
            {mockWebsite.redFlags.map((flag, idx) => (
              <div key={idx} className="pl-2">
                • {flag}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-200 dark:bg-gray-700 p-2 text-xs text-gray-600 dark:text-gray-400 text-center">
        ⚠️ Ceci est une simulation de site frauduleux
      </div>
    </div>
  );

  const renderEmail = () => (
    <div className="h-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col">
      {/* Header email */}
      <div className="bg-gray-100 dark:bg-gray-700 border-b dark:border-gray-600 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">📧</span>
          <span className="font-bold text-sm text-gray-700 dark:text-gray-300">Email</span>
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <div className="mb-1">
            <span className="font-semibold">De:</span>{' '}
            {mockWebsite.content.from || 'support@banque-fr.com'}
          </div>
          <div className="font-semibold">
            <span className="font-semibold">Objet:</span>{' '}
            {mockWebsite.content.subject || 'Action requise'}
          </div>
        </div>
      </div>

      {/* Corps de l'email */}
      <div className="flex-1 p-4 overflow-auto bg-white dark:bg-gray-800">
        <div className="mb-4">
          <img
            src="/api/placeholder/150/40"
            alt="Logo"
            className="h-8 mb-4 bg-blue-600 px-4 py-1 rounded text-white text-xs flex items-center"
          />
        </div>

        <div className="prose dark:prose-invert text-sm">
          <p className="mb-3 text-gray-800 dark:text-gray-200">Cher(e) client(e),</p>
          <p className="mb-3 text-gray-800 dark:text-gray-200 whitespace-pre-line">
            {mockWebsite.content.body ||
              'Nous avons détecté une activité suspecte sur votre compte.\n\nPour des raisons de sécurité, veuillez vérifier vos informations en cliquant sur le lien ci-dessous dans les 24 heures.'}
          </p>

          {mockWebsite.content.buttons && mockWebsite.content.buttons.length > 0 && (
            <div className="my-4">
              {mockWebsite.content.buttons.map((button, idx) => (
                <button
                  key={idx}
                  className={`px-6 py-2 rounded font-bold text-white ${
                    button.style || 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {button.text}
                </button>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
            Cordialement,
            <br />
            L'équipe de sécurité
          </p>
        </div>
      </div>

      {/* Indicateurs de fraude */}
      {mockWebsite.redFlags && mockWebsite.redFlags.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 text-xs text-gray-600 dark:text-gray-300 border-t dark:border-gray-600">
          <div className="font-semibold mb-1">🚩 Signes suspects :</div>
          {mockWebsite.redFlags.map((flag, idx) => (
            <div key={idx} className="pl-2">
              • {flag}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSMS = () => (
    <div className="h-full bg-gradient-to-b from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col">
      {mockWebsite.content.iframeUrl ? (
        // Affichage via iframe du fichier HTML personnalisé
        <>
          <div className="flex-1 bg-transparent rounded-t-lg overflow-hidden">
            <iframe
              src={mockWebsite.content.iframeUrl}
              className="w-full h-full border-0"
              title="Simulation SMS - Exemple visuel d'arnaque"
              sandbox="allow-scripts allow-same-origin"
            />
          </div>

          {/* Indicateurs de fraude */}
          {mockWebsite.redFlags && mockWebsite.redFlags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 p-3 text-xs text-gray-600 dark:text-gray-300 border-t dark:border-gray-700">
              <div className="font-semibold mb-1">🚩 Signes suspects :</div>
              {mockWebsite.redFlags.map((flag, idx) => (
                <div key={idx} className="pl-2">
                  • {flag}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        // Affichage par défaut (ancien rendu)
        <>
          {/* Header téléphone */}
          <div className="bg-white dark:bg-gray-800 p-3 text-center border-b dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">📱 Téléphone</div>
            <div className="font-semibold text-sm text-gray-700 dark:text-gray-300">
              {mockWebsite.content.sender || '+33 06 XX XX XX XX'}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-auto">
            <div className="flex flex-col items-start max-w-[85%]">
              <div className="bg-gray-300 dark:bg-gray-700 rounded-2xl rounded-tl-sm p-3 mb-2">
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
                  {mockWebsite.content.message ||
                    'Votre colis est en attente. Frais de livraison impayés: 2.99€\nRéglez via: https://colis-livraison.xyz/track'}
                </p>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Maintenant</div>
            </div>

            {mockWebsite.content.urgency && (
              <div className="mt-3 bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500 p-2 text-xs text-red-800 dark:text-red-300">
                {mockWebsite.content.urgency}
              </div>
            )}
          </div>

          {/* Indicateurs de fraude */}
          {mockWebsite.redFlags && mockWebsite.redFlags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 p-3 text-xs text-gray-600 dark:text-gray-300 border-t dark:border-gray-700">
              <div className="font-semibold mb-1">🚩 Signes suspects :</div>
              {mockWebsite.redFlags.map((flag, idx) => (
                <div key={idx} className="pl-2">
                  • {flag}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderPopup = () => (
    <div className="h-full rounded-lg shadow-xl overflow-hidden flex flex-col">
      <div className="flex-1 bg-white rounded-t-lg overflow-hidden">
        <iframe
          src="/fake-support-popup.html"
          className="w-full h-full border-0"
          title="Faux support technique - Simulation"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>

      {/* Indicateurs de fraude */}
      {mockWebsite.redFlags && mockWebsite.redFlags.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-700 p-3 text-xs text-gray-600 dark:text-gray-300 border-t">
          <div className="font-semibold mb-1">🚩 Signes suspects :</div>
          {mockWebsite.redFlags.map((flag, idx) => (
            <div key={idx} className="pl-2">
              • {flag}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSocial = () => (
    <div className="h-full bg-gray-100 dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden flex flex-col">
      {/* Header réseau social */}
      <div className="bg-blue-600 text-white p-3 flex items-center gap-3">
        <span className="text-2xl">👥</span>
        <span className="font-bold text-lg">SocialBook</span>
      </div>

      {/* Post */}
      <div className="flex-1 p-4 overflow-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-3">
          {/* Auteur du post */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
              {mockWebsite.content.from?.[0] || 'A'}
            </div>
            <div>
              <div className="font-semibold text-gray-800 dark:text-white">
                {mockWebsite.content.from || 'Ami en détresse'}
              </div>
              <div className="text-xs text-gray-500">Il y a 2 minutes</div>
            </div>
          </div>

          {/* Contenu */}
          <p className="text-gray-800 dark:text-gray-200 mb-3 whitespace-pre-line">
            {mockWebsite.content.message || 'Urgent ! Mon compte a été piraté, aide-moi !'}
          </p>

          {mockWebsite.content.urgency && (
            <div className="bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500 p-2 mb-3 text-sm text-red-800 dark:text-red-300">
              {mockWebsite.content.urgency}
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex gap-2 pt-3 border-t dark:border-gray-700">
            <button className="flex-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 py-2 rounded">
              👍 J'aime
            </button>
            <button className="flex-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 py-2 rounded">
              💬 Commenter
            </button>
          </div>
        </div>
      </div>

      {/* Indicateurs de fraude */}
      {mockWebsite.redFlags && mockWebsite.redFlags.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 text-xs text-gray-600 dark:text-gray-300 border-t">
          <div className="font-semibold mb-1">🚩 Signes suspects :</div>
          {mockWebsite.redFlags.map((flag, idx) => (
            <div key={idx} className="pl-2">
              • {flag}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full">
      {mockWebsite.type === 'ecommerce' && renderEcommerce()}
      {mockWebsite.type === 'email' && renderEmail()}
      {mockWebsite.type === 'sms' && renderSMS()}
      {mockWebsite.type === 'popup' && renderPopup()}
      {mockWebsite.type === 'social' && renderSocial()}
    </div>
  );
};
